/**
 * Per-artifact stale-draft detection for the workflow designer.
 *
 * A workflow has several independently-editable draft files that each shadow a published
 * counterpart. Staleness MUST be decided per artifact: a newer published *workflow* must
 * never cause an independently-edited draft (e.g. a newly-added `hostedAgentConnections`
 * entry) to be discarded, because those drafts can carry unpublished changes that would
 * otherwise be silently dropped on the next autosave.
 *
 * Scope: this helper only covers artifacts that live in the **workflow folder**
 * (`workflow(-draft).json`, `notes(-draft).json`), because it is fed the workflow-folder
 * VFS listing (`getDraftWorkflow` → `fetchWorkflowFilesWithMetadata`, which lists
 * `/vfs/{workflowName}`). `connections(-draft).json` and `parameters(-draft).json` live at
 * the **app root** (`/vfs/…`), so their mtimes are NOT in that listing and their staleness
 * cannot be computed here. Those drafts are instead preserved and reconciled by
 * content-equality (`!isEqual(draft, prod)`) in the caller — the safe direction for the
 * data-loss bug this fixes (better to keep a possibly-stale draft than silently discard
 * unpublished edits). If per-mtime staleness is ever needed for the app-root artifacts,
 * fetch their metadata separately and compare it there rather than widening this helper.
 *
 * See bug: hostedAgentConnections silently disappearing from connections.json.
 */

import { isFileModifiedBefore } from '@/services/vfsService';
import type { VFSFileMap } from '@/types/vfs';

/**
 * Whether each workflow-folder draft artifact is stale (its published counterpart is
 * newer). Only artifacts whose mtimes are actually present in the workflow-folder listing
 * are included — see the module doc for why connections/parameters are excluded.
 */
export interface DraftArtifactStaleness {
  workflow: boolean;
  notes: boolean;
}

/**
 * Compute per-artifact staleness from the workflow-folder VFS metadata. Each draft file is
 * compared only against its own published counterpart. Missing/unparseable timestamps fail
 * safe to `false` (treated as not-stale) via {@link isFileModifiedBefore}, so we never
 * discard a draft we can't prove is stale.
 */
export const computeDraftArtifactStaleness = (files: VFSFileMap): DraftArtifactStaleness => ({
  workflow: isFileModifiedBefore(files, 'workflow-draft.json', 'workflow.json'),
  notes: isFileModifiedBefore(files, 'notes-draft.json', 'notes.json'),
});
